import { Enrollment, EnrollmentDto } from './Enrollment';
import { utilities } from 'utils/utilities';

export interface AppointmentEnrollmentDto extends EnrollmentDto {
  appointmentTimeUtc: string | null;
  location: string | null;
  durationMinutes: number;
  suggestedTimesJson: string | null;
  attendanceOutcome: string | null;
  isAwaitingTeamInput: boolean;
}

/**
 * Enrollment for scheduling and tracking an appointment.
 * Created by AppointmentNodeHandler.
 */
export class AppointmentEnrollment extends Enrollment {
  /** ISO date-time string or null when not yet scheduled. */
  appointmentTimeUtc: string | null;
  location: string | null;
  durationMinutes: number;
  /** JSON array of date-time strings for suggested time slots. */
  suggestedTimesJson: string | null;
  /** "Continue" | "NoShow" | "Reschedule" | "AddAnother" */
  attendanceOutcome: string | null;
  isAwaitingTeamInput: boolean;

  readonly type: 'AppointmentEnrollment' = 'AppointmentEnrollment';

  constructor(
    id: string,
    appointmentTimeUtc: string | null = null,
    location: string | null = null,
    durationMinutes: number = 60,
    suggestedTimesJson: string | null = null,
    attendanceOutcome: string | null = null,
    isAwaitingTeamInput: boolean = true
  ) {
    super(id);
    this.appointmentTimeUtc = appointmentTimeUtc;
    this.location = location;
    this.durationMinutes = durationMinutes;
    this.suggestedTimesJson = suggestedTimesJson;
    this.attendanceOutcome = attendanceOutcome;
    this.isAwaitingTeamInput = isAwaitingTeamInput;
  }

  toDto(): AppointmentEnrollmentDto {
    return {
      appointmentTimeUtc: this.appointmentTimeUtc,
      location: this.location,
      durationMinutes: this.durationMinutes,
      suggestedTimesJson: this.suggestedTimesJson,
      attendanceOutcome: this.attendanceOutcome,
      isAwaitingTeamInput: this.isAwaitingTeamInput,
      id: this.id,
      enrollmentType: this.type,
      dtoType: 'Models.Api.Dtos.AppointmentEnrollmentDto',
      nodeInstanceId: this.nodeInstanceId,
      requiredSubmissionType: this.requiredSubmissionType,
      status: this.status,
      satisfiedAt: this.satisfiedAt,
    };
  }

  static fromDto(dto?: Partial<AppointmentEnrollmentDto> | AppointmentEnrollment | any): AppointmentEnrollment {
    if (!dto) {
      return new AppointmentEnrollment(utilities.DEFAULT_GUID);
    }

    if (dto instanceof AppointmentEnrollment) {
      return dto;
    }

    const id = dto.id ?? utilities.DEFAULT_GUID;
    return new AppointmentEnrollment(
      id,
      dto.appointmentTimeUtc ?? null,
      dto.location ?? null,
      dto.durationMinutes ?? 60,
      dto.suggestedTimesJson ?? null,
      dto.attendanceOutcome ?? null,
      dto.isAwaitingTeamInput ?? true
    );
  }
}

// Self-register to avoid Enrollment needing a hard import
Enrollment.register('AppointmentEnrollment', (d?: any) => AppointmentEnrollment.fromDto(d));
